/**
 * Phase 27.33 — Enrollment Platforms FAB launcher.
 *
 * Rendered globally by <App /> so every signed-in page has one-click
 * access to the KR-curated list of external enrollment platforms.
 *
 * UX:
 *   - Fixed floating pill at the bottom-right of every page.
 *   - Click → opens a modal with a grid of platform cards (logo +
 *     name + optional description). Clicking a card opens the URL
 *     in a new tab.
 *   - Admins see a "Manage" tab inside the same modal for inline
 *     add/edit/delete (name, description, url, category, sort_order,
 *     logo upload).
 *   - Auto-hides on the public landing page (`/connect/:slug`) and
 *     when the user isn't authenticated.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import {
  LayoutGrid, X, ExternalLink, Plus, Trash2, Upload, Loader2, Save,
  Pencil, Eye, EyeOff, Rocket,
} from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { enrollmentPlatformsAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

function absoluteLogo(url) {
  if (!url) return null;
  if (url.startsWith('http') || url.startsWith('blob:')) return url;
  return `${BACKEND}${url}`;
}

export default function EnrollmentPlatformsLauncher() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('grid'); // 'grid' | 'manage'

  // Route/auth gate — never render on public routes or before login.
  // Public routes (marketing site, standalone public tools, auth flows).
  // Keep this list in sync with the public <Route> entries in App.js.
  const path = location.pathname;
  const publicExact = new Set([
    '/', '/become-an-agent', '/events',
    '/login', '/forgot-password',
    '/terms', '/privacy', '/refund-policy',
    '/aep', '/operations-center', '/sms-opt-in',
  ]);
  const publicPrefixes = [
    '/connect/',
    '/reset-password/',
    '/email-preferences/',
    '/events/share/',
  ];
  // Public event detail is /events/:id — a single segment after /events/,
  // but /events/:id/workspace is protected and must keep the launcher.
  const isPublicEventDetail = /^\/events\/[^/]+$/.test(path) && !path.startsWith('/events/share/');
  const isPublicRoute = publicExact.has(path)
    || publicPrefixes.some((p) => path.startsWith(p))
    || isPublicEventDetail;
  const isAdmin = useMemo(() => {
    const roles = (user?.roles || []).map((r) => r.role);
    return roles.some((r) => r === 'KR_SuperAdmin' || r === 'KR_Admin');
  }, [user]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await enrollmentPlatformsAPI.list();
      setPlatforms(r.data.platforms || []);
    } catch (e) {
      // Silently ignore — the launcher shouldn't be noisy.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  if (!user || isPublicRoute) return null;

  // Group by category for the grid view.
  const grouped = platforms.reduce((acc, p) => {
    const key = (p.category || '').trim() || 'General';
    (acc[key] = acc[key] || []).push(p);
    return acc;
  }, {});

  return (
    <>
      {/* Floating launcher pill */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-40 group flex items-center gap-2 bg-slate-900 text-white pl-3 pr-4 py-2.5 rounded-full shadow-lg hover:shadow-xl hover:bg-slate-800 transition-all"
        data-testid="enrollment-launcher-fab"
        aria-label="Open enrollment platforms"
      >
        <span className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center">
          <Rocket size={15} />
        </span>
        <span className="text-sm font-semibold pr-1 hidden sm:inline">Platforms</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden" data-testid="enrollment-launcher-modal">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
            <div>
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <LayoutGrid size={18} className="text-slate-700" />
                Enrollment Platforms
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                One-click access to all enrollment tools. Opens in a new tab.
              </p>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1">
                <button
                  className={`px-3 py-1 text-xs font-medium rounded ${mode === 'grid' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:text-slate-900'}`}
                  onClick={() => setMode('grid')}
                  data-testid="launcher-mode-grid"
                >
                  Grid
                </button>
                <button
                  className={`px-3 py-1 text-xs font-medium rounded ${mode === 'manage' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:text-slate-900'}`}
                  onClick={() => setMode('manage')}
                  data-testid="launcher-mode-manage"
                >
                  Manage
                </button>
              </div>
            )}
          </div>

          {/* Body */}
          <div className="p-6 max-h-[70vh] overflow-y-auto">
            {loading ? (
              <div className="flex items-center gap-2 justify-center text-slate-500 py-12">
                <Loader2 className="animate-spin" size={18} /> Loading…
              </div>
            ) : mode === 'manage' && isAdmin ? (
              <ManageMode platforms={platforms} onChanged={load} />
            ) : platforms.length === 0 ? (
              <EmptyState isAdmin={isAdmin} onManage={() => setMode('manage')} />
            ) : (
              <div className="space-y-6">
                {Object.entries(grouped).map(([category, items]) => (
                  <div key={category}>
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
                      {category}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                      {items.map((p) => (
                        <PlatformCard key={p.platform_id} platform={p} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PlatformCard({ platform }) {
  const logo = absoluteLogo(platform.logo_url);
  const initials = platform.name.split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
  return (
    <a
      href={platform.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group border border-slate-200 rounded-xl p-4 hover:border-slate-900 hover:shadow-md transition bg-white flex flex-col items-center text-center gap-2"
      data-testid={`launcher-platform-${platform.platform_id}`}
    >
      <div className="w-full aspect-[2/1] max-h-24 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden">
        {logo ? (
          <img
            src={logo}
            alt={`${platform.name} logo`}
            className="w-full h-full object-contain"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <span className="text-slate-500 font-bold text-lg">{initials}</span>
        )}
      </div>
      <div className="text-sm font-semibold text-slate-900 leading-tight">{platform.name}</div>
      {platform.description && (
        <div className="text-[11px] text-slate-500 line-clamp-2">{platform.description}</div>
      )}
      <div className="flex items-center gap-1 text-[11px] text-slate-400 group-hover:text-slate-900 mt-1">
        <ExternalLink size={11} /> Open
      </div>
    </a>
  );
}

function EmptyState({ isAdmin, onManage }) {
  return (
    <div className="py-12 text-center">
      <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
        <LayoutGrid className="text-slate-400" size={24} />
      </div>
      <h3 className="text-base font-semibold text-slate-900">No platforms yet</h3>
      <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">
        {isAdmin
          ? 'Add your first enrollment platform link — you can include a name, URL, description, category, and logo.'
          : 'The admin hasn\'t added any enrollment platforms yet. Check back soon.'}
      </p>
      {isAdmin && (
        <Button className="mt-4" onClick={onManage} data-testid="launcher-empty-manage">
          <Plus size={14} className="mr-1" /> Add platform
        </Button>
      )}
    </div>
  );
}

// ── Admin management ────────────────────────────────────────────────

function ManageMode({ platforms, onChanged }) {
  const [showNew, setShowNew] = useState(false);
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600">
          {platforms.length} platform{platforms.length === 1 ? '' : 's'} configured
        </div>
        <Button size="sm" onClick={() => setShowNew(true)} data-testid="launcher-add-btn">
          <Plus size={14} className="mr-1" /> Add platform
        </Button>
      </div>
      {showNew && (
        <PlatformRow
          isNew
          onSaved={() => { setShowNew(false); onChanged(); }}
          onCancel={() => setShowNew(false)}
        />
      )}
      <div className="space-y-2">
        {platforms.map((p) => (
          <PlatformRow key={p.platform_id} platform={p} onSaved={onChanged} />
        ))}
      </div>
    </div>
  );
}

function PlatformRow({ platform, isNew = false, onSaved, onCancel }) {
  const [editing, setEditing] = useState(isNew);
  const [form, setForm] = useState({
    name: platform?.name || '',
    url: platform?.url || '',
    description: platform?.description || '',
    category: platform?.category || '',
    sort_order: platform?.sort_order ?? 100,
    is_active: platform?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileRef = useRef();

  const save = async () => {
    if (!form.name.trim() || !form.url.trim()) {
      toast.error('Name and URL are required');
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await enrollmentPlatformsAPI.create(form);
        toast.success('Platform added');
      } else {
        await enrollmentPlatformsAPI.update(platform.platform_id, form);
        toast.success('Saved');
      }
      onSaved?.();
      if (!isNew) setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${platform.name}"?`)) return;
    try {
      await enrollmentPlatformsAPI.remove(platform.platform_id);
      toast.success('Deleted');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const toggleActive = async () => {
    try {
      await enrollmentPlatformsAPI.update(platform.platform_id, { is_active: !platform.is_active });
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (isNew) {
      toast.info('Save the platform first, then upload the logo.');
      return;
    }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await enrollmentPlatformsAPI.uploadLogo(platform.platform_id, fd);
      toast.success('Logo uploaded');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingLogo(false);
    }
  };

  const logo = absoluteLogo(platform?.logo_url);

  if (!editing && platform) {
    // Read-only summary row.
    return (
      <div
        className={`flex items-center gap-3 p-3 border rounded-lg ${platform.is_active ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-200 opacity-70'}`}
        data-testid={`launcher-row-${platform.platform_id}`}
      >
        <div className="w-20 h-10 rounded bg-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0">
          {logo ? (
            <img src={`${logo}?v=${platform.updated_at}`} alt="" className="w-full h-full object-contain" loading="lazy" decoding="async" />
          ) : (
            <span className="text-xs font-semibold text-slate-500">
              {platform.name.slice(0, 2).toUpperCase()}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="font-semibold text-slate-900 truncate">{platform.name}</div>
            {platform.category && (
              <Badge variant="outline" className="text-[10px]">{platform.category}</Badge>
            )}
            {!platform.is_active && <Badge variant="outline" className="text-[10px] bg-slate-100">Hidden</Badge>}
          </div>
          <div className="text-xs text-slate-500 truncate">{platform.url}</div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button className="p-1.5 rounded hover:bg-slate-100" onClick={toggleActive}
            title={platform.is_active ? 'Hide from launcher' : 'Show in launcher'}
            data-testid={`launcher-toggle-${platform.platform_id}`}>
            {platform.is_active ? <Eye size={15} /> : <EyeOff size={15} />}
          </button>
          <button className="p-1.5 rounded hover:bg-slate-100" onClick={() => setEditing(true)}
            data-testid={`launcher-edit-${platform.platform_id}`}>
            <Pencil size={15} />
          </button>
          <button className="p-1.5 rounded hover:bg-red-50 text-red-600" onClick={remove}
            data-testid={`launcher-delete-${platform.platform_id}`}>
            <Trash2 size={15} />
          </button>
        </div>
      </div>
    );
  }

  // Editing / new form.
  return (
    <div className="border border-slate-300 rounded-lg p-4 bg-white space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <div className="text-xs font-semibold text-slate-600 mb-1">Name *</div>
          <Input value={form.name}
            placeholder="e.g. Sunfire"
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="launcher-form-name" />
        </div>
        <div>
          <div className="text-xs font-semibold text-slate-600 mb-1">Category</div>
          <Input value={form.category}
            placeholder="e.g. Medicare, ACA, Ancillary"
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            data-testid="launcher-form-category" />
        </div>
      </div>
      <div>
        <div className="text-xs font-semibold text-slate-600 mb-1">URL *</div>
        <Input value={form.url} placeholder="https://…"
          onChange={(e) => setForm({ ...form, url: e.target.value })}
          data-testid="launcher-form-url" />
      </div>
      <div>
        <div className="text-xs font-semibold text-slate-600 mb-1">Description (optional)</div>
        <Input value={form.description}
          placeholder="Short note — surfaces below the name in the launcher"
          onChange={(e) => setForm({ ...form, description: e.target.value })} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <div className="text-xs font-semibold text-slate-600 mb-1">Sort order (lower = first)</div>
          <Input type="number" value={form.sort_order}
            onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value || '100', 10) })} />
        </div>
        <label className="flex items-end pb-2 gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={form.is_active}
            onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
          Show in launcher
        </label>
      </div>

      {/* Logo upload (only after save) */}
      {!isNew && (
        <div className="pt-3 border-t border-slate-100">
          <div className="text-xs font-semibold text-slate-600 mb-1.5">Logo</div>
          <div className="flex items-center gap-3">
            <div className="w-24 h-12 rounded bg-slate-100 flex items-center justify-center overflow-hidden">
              {logo ? (
                <img src={`${logo}?v=${Date.now()}`} alt="" className="w-full h-full object-contain" loading="lazy" decoding="async" />
              ) : (
                <span className="text-xs text-slate-400">None</span>
              )}
            </div>
            <input type="file" accept="image/*" ref={fileRef}
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadLogo(f); e.target.value = ''; }}
              data-testid={`launcher-logo-input-${platform.platform_id}`} />
            <Button variant="outline" size="sm" type="button" onClick={() => fileRef.current?.click()} disabled={uploadingLogo}>
              {uploadingLogo ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Upload size={13} className="mr-1" />}
              {logo ? 'Replace' : 'Upload'}
            </Button>
            {logo && (
              <Button variant="ghost" size="sm" type="button" className="text-red-600"
                onClick={async () => {
                  try { await enrollmentPlatformsAPI.removeLogo(platform.platform_id); onSaved?.(); }
                  catch (e) { toast.error('Failed'); }
                }}>
                Remove
              </Button>
            )}
          </div>
          <p className="text-[10px] text-slate-400 mt-1">Wide 2:1 PNG/JPG works best (e.g. 600×300, transparent bg preferred, ≤ 4MB).</p>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-2">
        {(isNew || onCancel) && (
          <Button variant="ghost" size="sm" onClick={() => { onCancel?.(); if (!isNew) setEditing(false); }}>
            Cancel
          </Button>
        )}
        {!isNew && !onCancel && (
          <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
        )}
        <Button size="sm" onClick={save} disabled={saving} data-testid="launcher-save-btn">
          {saving ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Save size={13} className="mr-1" />}
          {isNew ? 'Add' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
